import React from 'react';
import styled, { keyframes } from 'styled-components';
import SEO from '../components/SEO';
import BackgroundDesign from '../components/BackgroundDesign';
import ProjectVideo from '../components/ProjectVideo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faUsers, faSchool, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.background || '#d7d7d7'};
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(25px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HeroBanner = styled.section`
  position: relative;
  min-height: 45vh;
  padding: 10rem 2rem 4rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.85)),
              url(${process.env.PUBLIC_URL}/images/sliders/MAIN-SLIDE-HBJv3.webp) center/cover no-repeat;
  color: white;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 11rem 1.25rem 3rem 1.25rem;
    min-height: 40vh;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(to top, #1a1a1a, transparent);
  }
`;

const HeroContent = styled.div`
  max-width: 850px;
  z-index: 2;
  animation: ${fadeUp} 0.8s ease-out;
`;

const PageTitle = styled.h1`
  font-size: clamp(2.4rem, 5vw, 4rem);
  font-weight: 800;
  margin-bottom: 1.25rem;
  text-transform: uppercase;
  letter-spacing: 2px;

  span {
    color: ${props => props.theme.colors.primary || '#990011'};
  }
`;

const PageSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
  max-width: 750px;
  margin: 0 auto;
`;

const DetailsSection = styled.section`
  padding: 4rem 0 6rem 0;
  background: #ffffff;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-size: 2.2rem;
    font-weight: 800;
    color: #1a1a1a;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.15rem;
    color: #555;
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.7;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: 2.5rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.theme.colors.primary || '#990011'};
  }

  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary || '#990011'};
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 2rem;
    font-weight: 800;
    color: #1a1a1a;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.95rem;
    color: #666;
    margin: 0;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary || '#990011'} 0%, #66000b 100%);
  border-radius: 24px;
  padding: 3.5rem 2rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  box-shadow: 0 15px 35px rgba(153, 0, 17, 0.3);

  h3 {
    font-size: 2.2rem;
    font-weight: 800;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.15rem;
    max-width: 650px;
    margin: 0 auto 2rem auto;
    opacity: 0.95;
    line-height: 1.6;
  }
`;

const DonateButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  color: ${props => props.theme.colors.primary || '#990011'};
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 800;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    background: #f8f9fa;
    color: #800000;
  }
`;

const ProjectVideoPage = () => {
  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="Project Overview Video | The Big Heart Association (TBH)"
        description="Watch the official project documentary explaining The Big Heart Association's initiatives, school renovations, medical caravans, and humanitarian actions across Morocco."
        url="/project-video"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Project Video', url: '/project-video' }
        ]}
      />

      <MainContent>
        <HeroBanner>
          <HeroContent>
            <PageTitle>PROJECT <span>DOCUMENTARY</span></PageTitle>
            <PageSubtitle>
              Watch how The Big Heart Association is driving sustainable education, healthcare support, and community empowerment across Morocco.
            </PageSubtitle>
          </HeroContent>
        </HeroBanner>

        {/* Main Video Embed Component */}
        <ProjectVideo isStandalone={true} />

        <DetailsSection>
          <Container>
            <SectionHeader>
              <h2>Empowering Rural Communities Across Morocco</h2>
              <p>
                Founded in February 2022 in Casablanca, The Big Heart Association is committed to creating lasting, positive change. Through our flagship 7-year Heartbeat Journey initiative, we aim to renovate 24 schools in Morocco's 12 regions, equip classrooms with digital technology, and deliver vital medical aid.
              </p>
            </SectionHeader>

            <StatsGrid>
              <StatCard>
                <FontAwesomeIcon icon={faSchool} />
                <h3>24</h3>
                <p>Target Schools to Renovate</p>
              </StatCard>

              <StatCard>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <h3>12</h3>
                <p>Regions Across Morocco</p>
              </StatCard>

              <StatCard>
                <FontAwesomeIcon icon={faUsers} />
                <h3>70+</h3>
                <p>Dedicated Active Members</p>
              </StatCard>

              <StatCard>
                <FontAwesomeIcon icon={faHeart} />
                <h3>20+</h3>
                <p>Humanitarian Actions Completed</p>
              </StatCard>
            </StatsGrid>

            <CTASection>
              <h3>Be Part of The Heartbeat Journey</h3>
              <p>
                Your support directly helps us renovate schools, supply educational materials, and provide medical care to families in need.
              </p>
              <DonateButton to="/donate">
                <FontAwesomeIcon icon={faHeart} /> Make a Difference Today
              </DonateButton>
            </CTASection>
          </Container>
        </DetailsSection>
      </MainContent>
    </PageWrapper>
  );
};

export default ProjectVideoPage;
